import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/useAuth';

/**
 * Guards the /admin route group: staff only, redirect others to /admin/login.
 *
 * Staff, not admins — an editor is meant to see the panel, just not change it.
 * Write access is enforced per-route on the API (`requireAdmin`), which is what
 * actually holds; hiding a button is courtesy, not security.
 */
export function RequireAdmin({ children }: { children: ReactNode }) {
  const { status, isStaff } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center text-slate-500">
        Checking access…
      </div>
    );
  }

  if (status !== 'authenticated' || !isStaff) {
    return <Navigate to="/admin/login" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}

/**
 * Guards the admin-only corners inside the panel (managing panel accounts).
 * An editor who types the URL gets sent to the dashboard rather than a 403 they
 * can do nothing about.
 */
export function RequireAdminRole({ children }: { children: ReactNode }) {
  const { status, isAdmin } = useAuth();

  if (status === 'loading') return null;
  if (!isAdmin) return <Navigate to="/admin" replace />;

  return <>{children}</>;
}
